import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, X, Calendar, User, Building2, FileText, Link as LinkIcon, ArrowLeft, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import './ModuloBase.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EditarGestion = ({ 
  gestion,
  onSuccess, 
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [instituciones, setInstituciones] = useState([]);
  const [asesores, setAsesores] = useState([]);
  const [indicadores, setIndicadores] = useState([]);
  const [tiposConcurso, setTiposConcurso] = useState([]);
  const [estadosConcurso, setEstadosConcurso] = useState([]);
  
  // Estados para modales de NOGs, NPGs y Proveedores
  const [showNogModal, setShowNogModal] = useState(false);
  const [showNpgModal, setShowNpgModal] = useState(false);
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  
  const [currentNog, setCurrentNog] = useState({
    nog: '', descripcion: '', tipo_concurso: '', status_concurso: '', fecha_adjudicacion: '', monto: ''
  });
  const [currentNpg, setCurrentNpg] = useState({
    npg: '', descripcion: '', tipo_concurso: '', status_concurso: '', fecha_adjudicacion: '', monto: ''
  });
  const [currentProveedor, setCurrentProveedor] = useState({
    nombre: '', nit: '', representante_legal: '', monto_adjudicado: ''
  });
  const [currentGuatecomprasLink, setCurrentGuatecomprasLink] = useState('');
  
  const [formData, setFormData] = useState({
    tipo_gestion: gestion?.tipo_gestion || '',
    institucion: gestion?.institucion || '',
    nombre_asesor_cnc: gestion?.nombre_asesor_cnc || '',
    nombre_asesor_instancia: gestion?.nombre_asesor_instancia || '',
    fecha_asignacion: gestion?.fecha_asignacion?.split('T')[0] || '',
    fecha_vencimiento: gestion?.fecha_vencimiento?.split('T')[0] || '',
    descripcion: gestion?.descripcion || '',
    links_referenciales: gestion?.links_referenciales || [],
    estado: gestion?.estado || 'Asignada',
    campos_especificos: {
      ...gestion?.campos_especificos,
      detalle_nogs: gestion?.campos_especificos?.detalle_nogs || [],
      detalle_npgs: gestion?.campos_especificos?.detalle_npgs || [],
      proveedores: gestion?.campos_especificos?.proveedores || [],
      enlaces_guatecompras: gestion?.campos_especificos?.enlaces_guatecompras || []
    }
  });

  const [currentLink, setCurrentLink] = useState('');

  useEffect(() => {
    fetchCatalogos();
  }, []);

  const fetchCatalogos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [instResponse, asesoresResponse, indicadoresResponse, tiposResponse, estadosResponse] = await Promise.all([
        axios.get(`${API}/catalogs/institutions`, { headers }),
        axios.get(`${API}/catalogos/asesores-cnc?activo=true`, { headers }),
        axios.get(`${API}/catalogos/indicadores-banderas-rojas?activo=true`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogs/tipo_concurso`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogs/estado_concurso`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setInstituciones((instResponse.data || []).filter(inst => inst.is_active));
      setAsesores(asesoresResponse.data || []);
      setIndicadores(indicadoresResponse.data || []);
      setTiposConcurso((tiposResponse.data || []).filter(t => t.is_active !== false));
      setEstadosConcurso((estadosResponse.data || []).filter(e => e.is_active !== false));
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  // Manejadores para NOGs
  const handleAddNog = () => {
    if (!currentNog.nog) {
      toast.error('El número NOG es requerido');
      return;
    }
    setFormData(prev => {
      const nuevosNogs = [...(prev.campos_especificos.detalle_nogs || []), {...currentNog}];
      // Solo actualizar cantidad_nog si es menor al nuevo count
      const nuevaCantidad = Math.max(
        prev.campos_especificos.cantidad_nog || 0,
        nuevosNogs.length
      );
      return {
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          detalle_nogs: nuevosNogs,
          cantidad_nog: nuevaCantidad
        }
      };
    });
    setCurrentNog({ nog: '', descripcion: '', tipo_concurso: '', status_concurso: '', fecha_adjudicacion: '', monto: '' });
    setShowNogModal(false);
    toast.success('NOG agregado');
  };

  const handleRemoveNog = (index) => {
    setFormData(prev => {
      const nuevosNogs = prev.campos_especificos.detalle_nogs.filter((_, i) => i !== index);
      return {
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          detalle_nogs: nuevosNogs,
          // No decrementar si la cantidad manual era mayor
          cantidad_nog: Math.max(nuevosNogs.length, prev.campos_especificos.cantidad_nog || 0)
        }
      };
    });
  };

  // Manejadores para NPGs
  const handleAddNpg = () => {
    if (!currentNpg.npg) {
      toast.error('El número NPG es requerido');
      return;
    }
    setFormData(prev => {
      const nuevosNpgs = [...(prev.campos_especificos.detalle_npgs || []), {...currentNpg}];
      // Solo actualizar cantidad_npg si es menor al nuevo count
      const nuevaCantidad = Math.max(
        prev.campos_especificos.cantidad_npg || 0,
        nuevosNpgs.length
      );
      return {
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          detalle_npgs: nuevosNpgs,
          cantidad_npg: nuevaCantidad
        }
      };
    });
    setCurrentNpg({ npg: '', descripcion: '', tipo_concurso: '', status_concurso: '', fecha_adjudicacion: '', monto: '' });
    setShowNpgModal(false);
    toast.success('NPG agregado');
  };

  const handleRemoveNpg = (index) => {
    setFormData(prev => {
      const nuevosNpgs = prev.campos_especificos.detalle_npgs.filter((_, i) => i !== index);
      return {
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          detalle_npgs: nuevosNpgs,
          // No decrementar si la cantidad manual era mayor
          cantidad_npg: Math.max(nuevosNpgs.length, prev.campos_especificos.cantidad_npg || 0)
        }
      };
    });
  };

  // Manejadores para Proveedores
  const handleAddProveedor = () => {
    if (!currentProveedor.nombre) {
      toast.error('El nombre del proveedor es requerido');
      return;
    }
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        proveedores: [...(prev.campos_especificos.proveedores || []), {...currentProveedor}]
      }
    }));
    setCurrentProveedor({ nombre: '', nit: '', representante_legal: '', monto_adjudicado: '' });
    setShowProveedorModal(false);
    toast.success('Proveedor agregado');
  };

  const handleRemoveProveedor = (index) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        proveedores: prev.campos_especificos.proveedores.filter((_, i) => i !== index)
      }
    }));
  };

  // Manejadores para enlaces Guatecompras
  const handleAddGuatecomprasLink = () => {
    if (!currentGuatecomprasLink) return;
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        enlaces_guatecompras: [...(prev.campos_especificos.enlaces_guatecompras || []), currentGuatecomprasLink]
      }
    }));
    setCurrentGuatecomprasLink('');
  };

  const handleRemoveGuatecomprasLink = (index) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        enlaces_guatecompras: prev.campos_especificos.enlaces_guatecompras.filter((_, i) => i !== index)
      }
    }));
  };

  // Manejador para cambio de indicador
  const handleIndicadorChange = (indicadorId) => {
    const indicador = indicadores.find(i => i.id === indicadorId);
    if (indicador) {
      setFormData(prev => ({
        ...prev,
        campos_especificos: {
          ...prev.campos_especificos,
          indicador_id: indicadorId,
          indicador_codigo: indicador.codigo || '',
          indicador_nombre: indicador.nombre_categoria || '',
          indicador_descripcion: indicador.indicador || ''
        }
      }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCampoEspecifico = (field, value) => {
    setFormData(prev => ({
      ...prev,
      campos_especificos: {
        ...prev.campos_especificos,
        [field]: value
      }
    }));
  };

  const handleAddLink = () => {
    if (currentLink.trim()) {
      setFormData(prev => ({
        ...prev,
        links_referenciales: [...prev.links_referenciales, currentLink.trim()]
      }));
      setCurrentLink('');
    }
  };

  const handleRemoveLink = (index) => {
    setFormData(prev => ({
      ...prev,
      links_referenciales: prev.links_referenciales.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/gestiones/${gestion.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gestión actualizada exitosamente');
      onSuccess();
    } catch (error) {
      console.error('Error al actualizar gestión:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar la gestión');
    } finally {
      setLoading(false);
    }
  };

  // Renderizar campos específicos según el tipo de gestión
  const renderCamposEspecificos = () => {
    const campos = formData.campos_especificos;
    const tipo = formData.tipo_gestion;

    if (tipo === 'Adquisiciones públicas' || tipo === 'Banderas Rojas') {
      return (
        <>
          {/* Sección 1: Información de la Bandera */}
          <div className="modulo-form-section">
            <h3 className="modulo-form-section-title">
              <AlertTriangle size={18} style={{ marginRight: '0.5rem', color: '#F44336' }} />
              Información de la Bandera
            </h3>
            <div className="form-grid">
              <div className="form-field">
                <label className="required">Color de Bandera</label>
                <select
                  value={campos.color_bandera || ''}
                  onChange={(e) => handleCampoEspecifico('color_bandera', e.target.value)}
                  required
                >
                  <option value="">Seleccione color</option>
                  <option value="Roja">🔴 Roja (Alto Riesgo)</option>
                  <option value="Amarilla">🟡 Amarilla (Riesgo Medio)</option>
                  <option value="Verde">🟢 Verde (Bajo Riesgo)</option>
                </select>
              </div>

              <div className="form-field">
                <label className="required">Indicador</label>
                <select
                  value={campos.indicador_id || ''}
                  onChange={(e) => handleIndicadorChange(e.target.value)}
                  required
                >
                  <option value="">Seleccione un indicador</option>
                  {indicadores.map((ind) => (
                    <option key={ind.id} value={ind.id}>
                      {ind.codigo ? `${ind.codigo} - ` : ''}{ind.nombre_categoria} - {ind.indicador?.substring(0, 50)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Fecha Inicio Análisis</label>
                <input
                  type="date"
                  value={campos.fecha_inicio_analisis || ''}
                  onChange={(e) => handleCampoEspecifico('fecha_inicio_analisis', e.target.value)}
                />
              </div>

              <div className="form-field">
                <label>Fecha Fin Análisis</label>
                <input
                  type="date"
                  value={campos.fecha_fin_analisis || ''}
                  onChange={(e) => handleCampoEspecifico('fecha_fin_analisis', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Sección 2: Datos de la Compra Pública */}
          <div className="modulo-form-section">
            <h3 className="modulo-form-section-title">Datos de la Compra Pública</h3>
            <div className="form-grid">
              <div className="form-field">
                <label className="required">Unidad Compradora</label>
                <input
                  type="text"
                  value={campos.unidad_compradora || ''}
                  onChange={(e) => handleCampoEspecifico('unidad_compradora', e.target.value)}
                  placeholder="Nombre de la unidad compradora"
                  required
                />
              </div>

              <div className="form-field">
                <label>Cantidad de NOG</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    value={campos.cantidad_nog ?? campos.detalle_nogs?.length ?? 0}
                    onChange={(e) => handleCampoEspecifico('cantidad_nog', parseInt(e.target.value) || 0)}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#666' }}>
                    (Detallados: {campos.detalle_nogs?.length || 0})
                  </span>
                </div>
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.7rem' }}>
                  Puede ingresar manualmente o agregar NOGs con el botón
                </small>
              </div>

              <div className="form-field">
                <label>Cantidad de NPG</label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="number"
                    min="0"
                    value={campos.cantidad_npg ?? campos.detalle_npgs?.length ?? 0}
                    onChange={(e) => handleCampoEspecifico('cantidad_npg', parseInt(e.target.value) || 0)}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#666' }}>
                    (Detallados: {campos.detalle_npgs?.length || 0})
                  </span>
                </div>
                <small style={{ color: 'var(--color-text-muted)', fontSize: '0.7rem' }}>
                  Puede ingresar manualmente o agregar NPGs con el botón
                </small>
              </div>
            </div>

            {/* Enlaces Guatecompras */}
            <div className="form-field" style={{ marginTop: '1rem' }}>
              <label>Enlaces Guatecompras</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="url"
                  value={currentGuatecomprasLink}
                  onChange={(e) => setCurrentGuatecomprasLink(e.target.value)}
                  placeholder="https://www.guatecompras.gt/..."
                  style={{ flex: 1 }}
                />
                <button type="button" className="btn btn-secondary" onClick={handleAddGuatecomprasLink}>
                  <Plus size={16} />
                  Agregar
                </button>
              </div>
              {campos.enlaces_guatecompras?.length > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  {campos.enlaces_guatecompras.map((link, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.25rem 0' }}>
                      <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.875rem', color: '#1976D2' }}>
                        {link}
                      </a>
                      <button type="button" onClick={() => handleRemoveGuatecomprasLink(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F44336' }}>
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sección 3: Detalle de NOGs */}
          <div className="modulo-form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 className="modulo-form-section-title" style={{ margin: 0 }}>Detalle de NOGs</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowNogModal(true)}>
                <Plus size={16} />
                Agregar NOG
              </button>
            </div>
            
            {campos.detalle_nogs?.length > 0 ? (
              <div className="table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>NOG</th>
                      <th>Descripción</th>
                      <th>Tipo Concurso</th>
                      <th>Estado</th>
                      <th>Fecha Adj.</th>
                      <th>Monto</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {campos.detalle_nogs.map((nog, index) => (
                      <tr key={index}>
                        <td><strong>{nog.nog}</strong></td>
                        <td>{nog.descripcion || 'N/A'}</td>
                        <td>{nog.tipo_concurso}</td>
                        <td>{nog.status_concurso}</td>
                        <td>{nog.fecha_adjudicacion ? new Date(nog.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A'}</td>
                        <td>{nog.monto ? `Q ${parseFloat(nog.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                        <td>
                          <button type="button" className="btn-icon btn-icon-danger" onClick={() => handleRemoveNog(index)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
                No hay NOGs. Use "Agregar NOG" para incluir detalles.
              </p>
            )}
          </div>

          {/* Sección 4: Detalle de NPGs */}
          <div className="modulo-form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 className="modulo-form-section-title" style={{ margin: 0 }}>Detalle de NPGs (Concursos)</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowNpgModal(true)}>
                <Plus size={16} />
                Agregar NPG
              </button>
            </div>
            
            {campos.detalle_npgs?.length > 0 ? (
              <div className="table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>NPG</th>
                      <th>Descripción</th>
                      <th>Tipo Concurso</th>
                      <th>Estado</th>
                      <th>Fecha Adj.</th>
                      <th>Monto</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {campos.detalle_npgs.map((npg, index) => (
                      <tr key={index}>
                        <td><strong>{npg.npg}</strong></td>
                        <td>{npg.descripcion || 'N/A'}</td>
                        <td>{npg.tipo_concurso}</td>
                        <td>{npg.status_concurso}</td>
                        <td>{npg.fecha_adjudicacion ? new Date(npg.fecha_adjudicacion).toLocaleDateString('es-ES') : 'N/A'}</td>
                        <td>{npg.monto ? `Q ${parseFloat(npg.monto).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                        <td>
                          <button type="button" className="btn-icon btn-icon-danger" onClick={() => handleRemoveNpg(index)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
                No hay NPGs. Use "Agregar NPG" para incluir detalles de concursos.
              </p>
            )}
          </div>

          {/* Sección 5: Proveedores */}
          <div className="modulo-form-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 className="modulo-form-section-title" style={{ margin: 0 }}>Datos de Proveedores</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowProveedorModal(true)}>
                <Plus size={16} />
                Agregar Proveedor
              </button>
            </div>
            
            {campos.proveedores?.length > 0 ? (
              <div className="table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Nombre Proveedor</th>
                      <th>NIT</th>
                      <th>Representante Legal</th>
                      <th>Monto Adjudicado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {campos.proveedores.map((prov, index) => (
                      <tr key={index}>
                        <td><strong>{prov.nombre}</strong></td>
                        <td>{prov.nit || 'N/A'}</td>
                        <td>{prov.representante_legal || 'N/A'}</td>
                        <td>{prov.monto_adjudicado ? `Q ${parseFloat(prov.monto_adjudicado).toLocaleString('es-GT', { minimumFractionDigits: 2 })}` : 'N/A'}</td>
                        <td>
                          <button type="button" className="btn-icon btn-icon-danger" onClick={() => handleRemoveProveedor(index)}>
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: '1rem' }}>
                No hay proveedores. Use "Agregar Proveedor" para incluir datos.
              </p>
            )}
          </div>

          {/* Sección 6: Hallazgo */}
          <div className="modulo-form-section">
            <h3 className="modulo-form-section-title">Hallazgo / Observaciones</h3>
            <div className="form-field">
              <RichTextEditor
                value={campos.hallazgo || ''}
                onChange={(value) => handleCampoEspecifico('hallazgo', value)}
                placeholder="Descripción detallada del hallazgo o las observaciones relevantes"
                minHeight="150px"
              />
            </div>
          </div>
        </>
      );
    }

    if (tipo === 'Monitoreo' || tipo === 'Monitoreo de Medios') {
      const TEMATICAS_SUGERIDAS = [
        'Corrupción en Compras y Contrataciones Públicas',
        'Sobreprecio y Direccionamiento de Contratos',
        'Uso Ineficiente de Recursos Públicos',
        'Malversación y Uso Indebido de Fondos Públicos',
        'Nepotismo',
        'Conflicto de Interés',
        'Plazas Fantasma',
        'Soborno, Cohecho y Extorsión',
        'Fraude y Falsificación Documental',
        'Delitos Informáticos',
        'Incumplimiento de Deberes',
        'Abuso de Autoridad',
        'Deficiencias de Control Interno',
        'Transparencia y Acceso a la Información',
        'Irregularidades Presupuestarias y Financieras',
        'Irregularidades en Programas Sociales',
        'Irregularidades en Subsidios Estatales',
        'Infraestructura y Obra Pública',
        'Gestión Deficiente de Proyectos',
        'Corrupción en Salud Pública',
        'Corrupción en Educación',
        'Corrupción en Seguridad y Sistema Penitenciario',
        'Abuso Policial',
        'Control y Regulación Deficiente',
        'Licencias, Permisos y Autorizaciones Irregulares',
        'Clientelismo y Uso Político de Recursos Públicos',
        'Enriquecimiento Ilícito',
        'Tráfico de Influencias',
        'Redes de Corrupción',
        'Fiscalización Legislativa',
        'Investigación Periodística',
        'Denuncia Ciudadana',
        'Gestión de Recursos Humanos',
        'Logística y Administración Institucional',
        'Seguridad Institucional',
        'Riesgos de Integridad',
        'Gobernanza y Rendición de Cuentas',
        'Cumplimiento Normativo',
        'Ética Pública',
        'Otros'
      ];
      return (
        <div className="form-grid">
          <div className="form-field">
            <label>Autor</label>
            <input
              type="text"
              value={campos.author || ''}
              onChange={(e) => handleCampoEspecifico('author', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Red Social</label>
            <select
              value={campos.network || ''}
              onChange={(e) => handleCampoEspecifico('network', e.target.value)}
            >
              <option value="">Seleccione</option>
              <option value="X">X (Twitter)</option>
              <option value="Facebook">Facebook</option>
              <option value="Instagram">Instagram</option>
              <option value="TikTok">TikTok</option>
              <option value="YouTube">YouTube</option>
            </select>
          </div>
          <div className="form-field">
            <label>Temática relacionada</label>
            <input
              type="text"
              list="tematicas-sugeridas-editar"
              data-testid="editar-tematica"
              value={campos.tematica || ''}
              onChange={(e) => handleCampoEspecifico('tematica', e.target.value)}
              placeholder="Seleccione o escriba la temática"
            />
            <datalist id="tematicas-sugeridas-editar">
              {TEMATICAS_SUGERIDAS.map(t => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>
          <div className="form-field">
            <label>Personas Mencionadas</label>
            <input
              type="text"
              value={campos.persons || ''}
              onChange={(e) => handleCampoEspecifico('persons', e.target.value)}
            />
          </div>
          <div className="form-field" style={{ gridColumn: '1 / -1' }}>
            <label>Enlace</label>
            <input
              type="url"
              value={campos.link || ''}
              onChange={(e) => handleCampoEspecifico('link', e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (tipo === 'Dirección Ejecutiva' || tipo === 'Gestión de Información') {
      return (
        <div className="form-grid">
          <div className="form-field">
            <label>Número o identificador de documento - Oficio</label>
            <input
              type="text"
              value={campos.numero_iap || ''}
              onChange={(e) => handleCampoEspecifico('numero_iap', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Solicitante</label>
            <input
              type="text"
              value={campos.solicitante || ''}
              onChange={(e) => handleCampoEspecifico('solicitante', e.target.value)}
            />
          </div>
          <div className="form-field">
            <label>Fecha Solicitud</label>
            <input
              type="date"
              value={campos.fecha_solicitud || ''}
              onChange={(e) => handleCampoEspecifico('fecha_solicitud', e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (tipo === 'Denuncias') {
      return (
        <div className="form-grid">
          <div className="form-field">
            <label>Tipo requerimiento</label>
            <select
              value={campos.tipo_denuncia || ''}
              onChange={(e) => handleCampoEspecifico('tipo_denuncia', e.target.value)}
            >
              <option value="">Seleccione</option>
              <option value="Insumos de información">Insumos de información</option>
              <option value="Coordinación interinstitucional">Coordinación interinstitucional</option>
              <option value="Directrices o lineamientos">Directrices o lineamientos</option>
            </select>
          </div>
          <div className="form-field">
            <label>Canal de Recepción</label>
            <select
              value={campos.canal_recepcion || ''}
              onChange={(e) => handleCampoEspecifico('canal_recepcion', e.target.value)}
            >
              <option value="">Seleccione</option>
              <option value="Telefónica">Telefónica</option>
              <option value="Escrita">Escrita</option>
              <option value="Reunión">Reunión</option>
              <option value="Medios oficiales">Medios oficiales</option>
            </select>
          </div>
          <div className="form-field">
            <label>Requiriente</label>
            <input
              type="text"
              value={campos.Requiriente || ''}
              onChange={(e) => handleCampoEspecifico('Requiriente', e.target.value)}
              placeholder="Opcional - Anónimo si vacío"
            />
          </div>
        </div>
      );
    }

    if (tipo === 'Casos previos' || tipo === 'Seguimiento') {
      return (
        <div className="form-grid">
          <div className="form-field">
            <label>Caso Origen</label>
            <input
              type="text"
              value={campos.caso_origen || ''}
              onChange={(e) => handleCampoEspecifico('caso_origen', e.target.value)}
              placeholder="Número de oficio o referencia"
            />
          </div>
          <div className="form-field">
            <label>Tipo de Seguimiento</label>
            <select
              value={campos.tipo_seguimiento || ''}
              onChange={(e) => handleCampoEspecifico('tipo_seguimiento', e.target.value)}
            >
              <option value="">Seleccione</option>
              <option value="Atención y seguimiento">Atención y seguimiento</option>
              <option value="Monitoreo">Monitoreo</option>
              <option value="Reapertura de averiguación">Reapertura de averiguación</option>
            </select>
          </div>
        </div>
      );
    }

    // Campos genéricos para otros tipos
    return (
      <div className="form-grid">
        {Object.entries(campos).map(([key, value]) => (
          <div className="form-field" key={key}>
            <label>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleCampoEspecifico(key, e.target.value)}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="editar-gestion-container">
      <div className="editar-header">
        <button className="btn-back" onClick={onCancel}>
          <ArrowLeft size={20} />
          Volver
        </button>
        <div>
          <h2>Editar Gestión: {gestion?.nomenclatura}</h2>
          <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>{formData.tipo_gestion}</p>
        </div>
      </div>

      <form className="modulo-form" onSubmit={handleSubmit}>
        <div className="modulo-form-section">
          <h3 className="modulo-form-section-title">Información General</h3>
          
          <div className="form-grid">
            <div className="form-field">
              <label className="required">
                <Building2 size={16} />
                Institución del Organismo Ejecutivo
              </label>
              <select
                value={formData.institucion}
                onChange={(e) => handleChange('institucion', e.target.value)}
                required
              >
                <option value="">Seleccione una institución</option>
                {instituciones.map((inst) => (
                  <option key={inst.id} value={inst.value}>
                    {inst.value}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label className="required">
                <User size={16} />
                Asesor CNC
              </label>
              <select
                value={formData.nombre_asesor_cnc}
                onChange={(e) => handleChange('nombre_asesor_cnc', e.target.value)}
                required
              >
                <option value="">Seleccione un asesor</option>
                {asesores.map((asesor) => (
                  <option key={asesor.id} value={asesor.nombre_completo}>
                    {asesor.nombre_completo}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>
                <User size={16} />
                Asesor Instancia de Probidad
              </label>
              <input
                type="text"
                value={formData.nombre_asesor_instancia}
                onChange={(e) => handleChange('nombre_asesor_instancia', e.target.value)}
                placeholder="Nombre del asesor"
              />
            </div>

            <div className="form-field">
              <label className="required">
                <Calendar size={16} />
                Fecha de Asignación
              </label>
              <input
                type="date"
                value={formData.fecha_asignacion}
                onChange={(e) => handleChange('fecha_asignacion', e.target.value)}
                required
              />
            </div>

            <div className="form-field">
              <label>
                <Calendar size={16} />
                Fecha de Vencimiento
              </label>
              <input
                type="date"
                value={formData.fecha_vencimiento}
                onChange={(e) => handleChange('fecha_vencimiento', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label className="required">Estado</label>
              <select
                value={formData.estado}
                onChange={(e) => handleChange('estado', e.target.value)}
                required
              >
                <option value="Asignada">Asignada</option>
                <option value="En proceso">En proceso</option>
                <option value="Completada">Completada</option>
              </select>
            </div>
          </div>

          <div className="form-field" style={{ marginTop: '1rem' }}>
            <label className="required">
              <FileText size={16} />
              Descripción
            </label>
            <RichTextEditor
              value={formData.descripcion}
              onChange={(value) => handleChange('descripcion', value)}
              placeholder="Descripción detallada del caso..."
              minHeight="150px"
            />
          </div>

          <div className="form-field" style={{ marginTop: '1rem' }}>
            <label>
              <LinkIcon size={16} />
              Links Referenciales
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="url"
                value={currentLink}
                onChange={(e) => setCurrentLink(e.target.value)}
                placeholder="https://ejemplo.com"
              />
              <button type="button" className="btn btn-secondary" onClick={handleAddLink}>
                Agregar
              </button>
            </div>
            {formData.links_referenciales.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                {formData.links_referenciales.map((link, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: 'var(--color-background)', borderRadius: '4px', marginBottom: '0.25rem' }}>
                    <LinkIcon size={14} />
                    <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: '0.875rem' }}>{link}</a>
                    <button type="button" onClick={() => handleRemoveLink(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F44336' }}>
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Campos Específicos */}
        {Object.keys(formData.campos_especificos).length > 0 && (
          <div className="modulo-form-section">
            <h3 className="modulo-form-section-title">Campos Específicos - {formData.tipo_gestion}</h3>
            {renderCamposEspecificos()}
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
            <X size={18} />
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            <Save size={18} />
            {loading ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </form>

      {/* Modal para agregar NOG */}
      {showNogModal && (
        <div className="modal-overlay" onClick={() => setShowNogModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Agregar Detalle de NOG</h3>
              <button className="btn-cerrar" onClick={() => setShowNogModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field">
                  <label className="required">NOG</label>
                  <input
                    type="text"
                    value={currentNog.nog}
                    onChange={(e) => setCurrentNog({...currentNog, nog: e.target.value})}
                    placeholder="Número de Operación Guatecompras"
                  />
                </div>
                <div className="form-field" style={{ gridColumn: 'span 2' }}>
                  <label>Descripción</label>
                  <input
                    type="text"
                    value={currentNog.descripcion}
                    onChange={(e) => setCurrentNog({...currentNog, descripcion: e.target.value})}
                    placeholder="Descripción del NOG"
                  />
                </div>
                <div className="form-field">
                  <label>Tipo Concurso</label>
                  <select
                    value={currentNog.tipo_concurso}
                    onChange={(e) => setCurrentNog({...currentNog, tipo_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {tiposConcurso.map(tipo => (
                      <option key={tipo.id} value={tipo.value}>{tipo.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Estado Concurso</label>
                  <select
                    value={currentNog.status_concurso}
                    onChange={(e) => setCurrentNog({...currentNog, status_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {estadosConcurso.map(estado => (
                      <option key={estado.id} value={estado.value}>{estado.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Fecha Adjudicación</label>
                  <input
                    type="date"
                    value={currentNog.fecha_adjudicacion}
                    onChange={(e) => setCurrentNog({...currentNog, fecha_adjudicacion: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Monto (Q)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentNog.monto}
                    onChange={(e) => setCurrentNog({...currentNog, monto: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNogModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAddNog}>
                Agregar NOG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar NPG */}
      {showNpgModal && (
        <div className="modal-overlay" onClick={() => setShowNpgModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Agregar Detalle de NPG (Concurso)</h3>
              <button className="btn-cerrar" onClick={() => setShowNpgModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field">
                  <label className="required">NPG</label>
                  <input
                    type="text"
                    value={currentNpg.npg}
                    onChange={(e) => setCurrentNpg({...currentNpg, npg: e.target.value})}
                    placeholder="Número de Proceso Guatecompras"
                  />
                </div>
                <div className="form-field" style={{ gridColumn: 'span 2' }}>
                  <label>Descripción</label>
                  <input
                    type="text"
                    value={currentNpg.descripcion}
                    onChange={(e) => setCurrentNpg({...currentNpg, descripcion: e.target.value})}
                    placeholder="Descripción del NPG"
                  />
                </div>
                <div className="form-field">
                  <label>Tipo Concurso</label>
                  <select
                    value={currentNpg.tipo_concurso}
                    onChange={(e) => setCurrentNpg({...currentNpg, tipo_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {tiposConcurso.map(tipo => (
                      <option key={tipo.id} value={tipo.value}>{tipo.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Estado Concurso</label>
                  <select
                    value={currentNpg.status_concurso}
                    onChange={(e) => setCurrentNpg({...currentNpg, status_concurso: e.target.value})}
                  >
                    <option value="">Seleccione</option>
                    {estadosConcurso.map(estado => (
                      <option key={estado.id} value={estado.value}>{estado.value}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Fecha Adjudicación</label>
                  <input
                    type="date"
                    value={currentNpg.fecha_adjudicacion}
                    onChange={(e) => setCurrentNpg({...currentNpg, fecha_adjudicacion: e.target.value})}
                  />
                </div>
                <div className="form-field">
                  <label>Monto (Q)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentNpg.monto}
                    onChange={(e) => setCurrentNpg({...currentNpg, monto: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowNpgModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAddNpg}>
                Agregar NPG
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar Proveedor */}
      {showProveedorModal && (
        <div className="modal-overlay" onClick={() => setShowProveedorModal(false)}>
          <div className="modal-compartir" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Agregar Proveedor</h3>
              <button className="btn-cerrar" onClick={() => setShowProveedorModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-field">
                  <label className="required">Nombre del Proveedor</label>
                  <input
                    type="text"
                    value={currentProveedor.nombre}
                    onChange={(e) => setCurrentProveedor({...currentProveedor, nombre: e.target.value})}
                    placeholder="Razón social"
                  />
                </div>
                <div className="form-field">
                  <label>NIT</label>
                  <input
                    type="text"
                    value={currentProveedor.nit}
                    onChange={(e) => setCurrentProveedor({...currentProveedor, nit: e.target.value})}
                    placeholder="Número de NIT"
                  />
                </div>
                <div className="form-field">
                  <label>Representante Legal</label>
                  <input
                    type="text"
                    value={currentProveedor.representante_legal}
                    onChange={(e) => setCurrentProveedor({...currentProveedor, representante_legal: e.target.value})}
                    placeholder="Nombre del representante"
                  />
                </div>
                <div className="form-field">
                  <label>Monto Adjudicado (Q)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentProveedor.monto_adjudicado}
                    onChange={(e) => setCurrentProveedor({...currentProveedor, monto_adjudicado: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowProveedorModal(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleAddProveedor}>
                Agregar Proveedor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditarGestion;
